import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { booksAPI, meetingAPI } from '../api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Search, Send, Loader2, Calendar, Users, Mail, ExternalLink } from 'lucide-react';
import { format, parseISO } from 'date-fns';

const AllNotices = () => {
  const navigate = useNavigate();
  const [notices, setNotices] = useState([]);
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const [noticeRes, meetingRes] = await Promise.all([
          booksAPI.getNoticeLog(),
          meetingAPI.getAll(),
        ]);
        setNotices(noticeRes.data || []);
        setMeetings(meetingRes.data || []);
      } catch (err) {
        console.error('Failed to fetch notices:', err);
      } finally { setLoading(false); }
    })();
  }, []);

  const getMeetingTitle = (meetingId) => meetings.find(m => m.id === meetingId)?.title || 'Unknown Meeting';

  const filtered = notices.filter(n =>
    (n.subject || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    getMeetingTitle(n.meeting_id).toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) return <div className="flex items-center justify-center min-h-[60vh]"><Loader2 className="h-8 w-8 animate-spin text-[#00A3A3]" /></div>;

  return (
    <div className="space-y-6" data-testid="all-notices-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#1A1D21]">Notices</h1>
          <p className="text-[#5A6570] mt-1">All meeting notices sent to members and stakeholders</p>
        </div>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-[#5A6570]" />
        <Input placeholder="Search notices..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
          className="pl-10 border-[#E2E6EA]" data-testid="search-notices" />
      </div>

      {filtered.length === 0 ? (
        <Card className="border-[#E2E6EA]">
          <CardContent className="py-12 text-center">
            <Send className="h-10 w-10 mx-auto mb-3 text-[#A8B1B9]" />
            <h3 className="text-base font-medium text-[#1A1D21] mb-1">No notices found</h3>
            <p className="text-sm text-[#5A6570]">{searchTerm ? 'Try adjusting your search' : 'Notices are sent from meeting detail pages'}</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {filtered.sort((a, b) => new Date(b.sent_at || b.created_at || 0) - new Date(a.sent_at || a.created_at || 0)).map((notice, idx) => (
            <Card key={notice.id || idx} className="border-[#E2E6EA] hover:border-[#00A3A3]/50 transition-colors" data-testid={`notice-${notice.id || idx}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-semibold text-[#1A1D21] truncate">{notice.subject || 'Untitled Notice'}</h3>
                    <div className="flex flex-wrap items-center gap-3 mt-1.5 text-xs text-[#5A6570]">
                      <span className="flex items-center gap-1"><Calendar className="h-3 w-3" />{notice.sent_at ? format(parseISO(notice.sent_at), 'MMM d, yyyy h:mm a') : 'N/A'}</span>
                      {notice.meeting_id && (
                        <span className="flex items-center gap-1"><ExternalLink className="h-3 w-3" />{getMeetingTitle(notice.meeting_id)}</span>
                      )}
                      {notice.recipient_count && (
                        <span className="flex items-center gap-1"><Users className="h-3 w-3" />{notice.recipient_count} recipients</span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Badge variant="outline" className="text-[10px] bg-green-50 text-green-700 border-green-200">
                      <Mail className="h-3 w-3 mr-1" />Sent
                    </Badge>
                    {notice.meeting_id && (
                      <Button size="sm" variant="outline" className="h-7 text-xs"
                        onClick={() => navigate(`/meetings/${notice.meeting_id}/notices`)}
                        data-testid={`view-notice-${notice.id || idx}`}>
                        View
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default AllNotices;
